import {
	DefaultDropdownEntry,
	DefaultDropdownMenu,
	Dropdown,
} from '@dealstack/frontend/shared/components/fundament/Dropdown/Dropdown';
import { DropdownWrapper } from '@dealstack/frontend/shared/components/fundament/Dropdown/DropdownWrapper';
import { GroupWrapper } from '@dealstack/frontend/shared/components/fundament/GroupWrapper/GroupWrapper';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import React, { FunctionComponentElement, memo, useState } from 'react';
import { RegisterOptions, useController } from 'react-hook-form';
import styled from 'styled-components';
import { IGroupWrapperProps } from '@dealstack/frontend/shared/components/fundament/GroupWrapper/GroupWrapper.types';

interface ISelectProps extends IGroupWrapperProps {
	className?: string;
	children?: React.ReactNode;
	name: string;
	placeholder?: string;
	rhfOptions?: RegisterOptions;
	disabled?: boolean;
	borderLess?: boolean;
	maxSize?: boolean;
	open?: boolean;
}

interface CloneExpandedProps {
	onClick: unknown;
}
const Select = ({
	className,
	children,
	name,
	hint,
	placeholder,
	label,
	disabled,
	rhfOptions,
	open,
}: ISelectProps) => {
	const [isOpen, setIsOpen] = useState(open ?? false);
	const {
		field,
		fieldState: { error },
	} = useController({
		name,
		rules: rhfOptions,
	});

	const options = React.Children.map(children, (child) => {
		if (React.isValidElement(child)) {
			return {
				value: child.props.value,
				label: child.props.children,
			};
		}
		return null;
	});
	const value = options?.find((item) => item.value === field.value);
	const childrenWithProps = React.Children.map(children, (child) => {
		if (React.isValidElement(child)) {
			const { value } = child.props;
			return React.cloneElement<CloneExpandedProps>(
				child as FunctionComponentElement<CloneExpandedProps>,
				{
					onClick: () => {
						field.onChange(value);
						setIsOpen(false);
					},
				}
			);
		}
		return child;
	});
	return (
		<div className={className}>
			<GroupWrapper label={label} hint={hint} errors={error}>
				<InputWrapper
					onClick={() => {
						setIsOpen(!isOpen);
					}}
					isError={!!error}
				>
					{isOpen ? (
						<Dropdown
							align="left"
							onClose={() => {
								setIsOpen(false);
							}}
						>
							<DefaultDropdownMenu>{childrenWithProps}</DefaultDropdownMenu>
						</Dropdown>
					) : null}
					<input
						value={value?.label ?? ''}
						readOnly
						data-testid={`input-select-${name}`}
						disabled={disabled}
						placeholder={placeholder ? placeholder : 'Select'}
						onBlur={field.onBlur}
					/>
					<DropdownIcon disabled={disabled} variant="chevron-down" />
				</InputWrapper>
			</GroupWrapper>
		</div>
	);
};

export const Option = styled(
	({
		className,
		children,
		active,
		value,
		onClick,
		disabled,
	}: {
		active?: boolean;
		onClick?: () => unknown;
		className?: string;
		children: string;
		value: string | number;
		disabled?: boolean;
	}) => {
		const onClickAction = disabled ? null : onClick;
		return (
			<DefaultDropdownEntry
				active={active}
				className={className}
				onClick={onClickAction}
				value={value}
				disabled={disabled}
			>
				{children}
			</DefaultDropdownEntry>
		);
	}
)``;

const DropdownIcon = styled(Icon)`
	position: absolute;
	&.svg {
		--size: 1.2rem;
	}
`;

const InputWrapper = styled.div<{
	isError?: boolean;
}>`
	position: relative;
	width: 100%;

	${({ theme, isError }) =>
		isError
			? `
        input {
            border-color: ${theme.v2.color.error[600]} !important;
        }
    `
			: ``}

	${DropdownIcon} {
		top: 50%;
		right: 0;
		transform: translate(-0.7rem, -50%);

		&:active {
			transform: scale(0.95) translate(-0.7rem, -50%);
			transition: transform 0.05s linear;
		}
	}
`;
const Memoized = memo(Select);
const Styled = styled(Memoized)`
	display: flex;
	flex-direction: column;

	--placeholder-color: ${({ theme }) => theme.v2.color.neutral[500]};
	--text-color: ${({ theme }) => theme.v2.color.neutral[1100]};
	--border-color: ${({ theme }) => theme.v2.color.neutral[500]};
	--border-color-focused: ${({ theme }) => theme.v2.color.neutral[700]};
	${({ maxSize }) => (maxSize ? 'width: 100%;' : ``)}

	input,
    ${DropdownIcon} {
		cursor: pointer;
	}

	input {
		border: 1px solid var(--border-color);
		width: 100%;
		box-sizing: border-box;

		${({ borderLess }) =>
			borderLess
				? `
            border: none;
        `
				: ``}
		border-radius: 4px;
		padding: 0.6rem;
		font-size: 0.75rem;

		${({ theme, disabled }) =>
			disabled
				? `
                --text-color: ${theme.v2.color.neutral[500]};
                --border-color: ${theme.v2.color.neutral[400]};
                background-color: ${theme.v2.color.neutral[200]};
        `
				: ``}

		&:focus {
			border: 1px solid var(--border-color-focused);
		}
		caret-color: var(--text-color);
		color: var(--text-color);

		&::placeholder {
			color: var(--placeholder-color);
			opacity: 1; /* Firefox */
		}

		${({ theme, borderLess }) =>
			borderLess
				? `
            border: none;
            &:focus {
                border: none;
            }
            --placeholder-color: ${theme.v2.color.neutral[700]};
        `
				: ``}
	}

	${({ disabled }) =>
		disabled
			? `
            ${InputWrapper} {
                * {
                cursor: not-allowed !important;
                }
            }
    `
			: ``}

	${DropdownWrapper}, ${Dropdown} {
		width: 100%;
	}

	${DefaultDropdownMenu} {
		max-height: 15rem;
	}
`;

export { Styled as Select };
